import {
  FeasibilityQuota,
  FeasibilityQuotaGroupKey,
  FeasibilityQuotaSet,
  NESTED_QUOTA_LABEL,
  NESTED_QUOTA_TYPE,
  Quota,
  QuotaCriteria,
} from '@ps/survey-utils';
import { nonEmptyList } from '@ps/utils';
import { Option } from '@sapphire/result';

import {
  FeasibilityCriteriaDto,
  FeasibilityRangeQuotaDto,
  FeasibilityRangeSetDto,
} from '../dtos/feasibility.dto';

import { rangeSetsFor, targetsAnything } from './feasibility-criterion.helper';

/** The two outputs a set of quotas produces: the request rows, and the drawer's rows. */
export interface FeasibilityQuotaRows {
  readonly quotas: FeasibilityRangeQuotaDto[];
  readonly quotaSet: FeasibilityQuotaSet;
}

/**
 * A quota that targets something, with its primary criterion resolved once. The
 * payload row and drawer row are independent projections, not side effects.
 */
export class TargetedQuota {
  private constructor(
    private readonly _quota: Quota,
    private readonly _primaryCriterion: QuotaCriteria,
    private readonly _primaryRangeSets: Option<FeasibilityRangeSetDto[]>,
  ) {}

  /**
   * None for a quota that targets nothing — see targetsAnything. The primary is the
   * first criterion that actually targets, not simply the first: a partially-filled
   * nested quota carries a blank criterion whose labels and code would misdescribe it.
   */
  public static from(quota: Quota): Option<TargetedQuota> {
    return Option.from(
      quota.criteria.find((criterion) => targetsAnything(criterion)),
    ).map(
      (primary) => new TargetedQuota(quota, primary, rangeSetsFor(primary)),
    );
  }

  public ownCriteria(): readonly QuotaCriteria[] {
    return this._quota.criteria;
  }

  public qualificationCodes(): ReadonlySet<number> {
    return new Set(
      this._quota.criteria.map((criterion) => criterion.qualificationCode),
    );
  }

  /** An unnamed nested group ('' is this field's Null Object) still needs a stable bucket. */
  public groupKey(): FeasibilityQuotaGroupKey {
    return this._targetsOneQualification()
      ? this._primaryCriterion.qualificationCode
      : this._quota.groupKey || NESTED_QUOTA_LABEL;
  }

  /** Criteria are supplied, not derived: only the criteria mapper knows the survey context. */
  public toPayloadRow(
    criteria: FeasibilityCriteriaDto[],
  ): FeasibilityRangeQuotaDto {
    return {
      buyer_quota_id: this._quota.buyerQuotaId,
      criteria,
      required_count: this._quota.requiredCount,
      flexible: this._quota.flexible,
      // Classified on the quota's OWN criteria count, never the merged count.
      quota_type: this._targetsOneQualification()
        ? String(this._primaryCriterion.qualificationCode)
        : NESTED_QUOTA_TYPE,
    };
  }

  /** The drawer labels a row off the primary criterion only. */
  public toDrawerRow(): FeasibilityQuota {
    return {
      qualificationCode: this._primaryCriterion.qualificationCode,
      qualificationName: this._primaryCriterion.qualificationName,
      ...nonEmptyList(this._primaryCriterion.conditionCodes).mapOr(
        {},
        (codes) => ({ conditionCodes: [...codes] }),
      ),
      ...nonEmptyList(this._primaryCriterion.conditionNames).mapOr(
        {},
        (names) => ({ conditionNames: [...names] }),
      ),
      ...this._primaryRangeSets.mapOr({}, (rangeSets) => ({ rangeSets })),
      buyerQuotaId: this._quota.buyerQuotaId,
      requiredCount: this._quota.requiredCount,
    };
  }

  private _targetsOneQualification(): boolean {
    return this._quota.criteria.length === 1;
  }
}

/** Buckets drawer rows by group key, preserving the order the quotas arrived in. */
export function toQuotaSet(
  targetedQuotas: readonly TargetedQuota[],
): FeasibilityQuotaSet {
  const buckets = new Map<FeasibilityQuotaGroupKey, FeasibilityQuota[]>();

  targetedQuotas.forEach((quota) => {
    const groupKey = quota.groupKey();
    const drawerRow = quota.toDrawerRow();
    const bucket = buckets.get(groupKey);

    if (bucket) {
      bucket.push(drawerRow);
      return;
    }

    buckets.set(groupKey, [drawerRow]);
  });

  return buckets;
}
